import React, { useState } from "react";
import {
  Empty,
  Timeline,
  Typography,
  Card,
  Button,
  Tag,
  Modal,
  Radio,
  Table,
  Dropdown,
  Tooltip,
  Popover,
  Badge,
} from "antd";
import {
  FieldTimeOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  RightCircleOutlined,
  MailOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  WarningOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { Session, SessionStatus } from "../../../../Entities/Session";
import { InterviewRound } from "../../../../Dto/GenerateJobDescription.dto";
import dayjs from "dayjs";
import { JobDescriptionEntity } from "../../../../Entities/JobDescription";
import { JobApplication } from "../../../../Entities/JobApplication";
import useOrganization from "../../../../Hooks/useOrganization";
import { isProfileMatchCompleted } from "../../utils/job.application.helper.functions";
import { useAssessmentStatistics } from "../JobApplicationInterviewRounds/useAssessmentStatistics";
import useAccess from "../../../../Hooks/useAccess";
import SubscriptionWarning from "../../../../Components/SubscriptionWarning";
import { openNotification } from "../../../../Components/Notification";
import { UserProfile } from "../../../../Entities/UserProfile";

interface InterviewProgressProps {
  interviewSessions: Session[];
  interviewRounds: InterviewRound[];
  setActiveTabKey: React.Dispatch<React.SetStateAction<string>>;
  jobPost: JobDescriptionEntity;
  profile: UserProfile | null;
  setSelectedSession: React.Dispatch<React.SetStateAction<Session | null>>;
  jobApplication: JobApplication;
  handleMoveToAIAssessmentRound: () => Promise<void>;
  handleRestartAIAssessmentRound: () => Promise<void>;
  handleSendReminderNotification: (type: "profile.match" | "round") => Promise<void>;
}

type UnifiedRoundData = {
  roundId: string;
  roundNumber: number;
  displayRoundNumber: number;
  title: string;
  isMustQualify: boolean;
  passPercentage?: number;
  sessions: Session[];
  latestSession: Session | null;
  status: "not-attempted" | "pending" | "qualified" | "unqualified";
  hasAttempted: boolean;
  attemptNumber: number;
  isRestarted: boolean;
};

/** One timeline/table row per attempt (session) or per round when not attempted */
type AttemptItem = {
  roundId: string;
  displayRoundNumber: number;
  title: string;
  session: Session | null;
  attemptNumber: number;
  isRestarted: boolean;
  status: "not-attempted" | "pending" | "qualified" | "unqualified";
};

type TableRoundData = {
  key: string;
  sessionId: string;
  roundName: string;
  roundNumber: number;
  displayRoundNumber: number;
  interviewRoundId: string;
  attemptNumber: number;
  isRestarted: boolean;
  status: SessionStatus | "not-attempted";
  totalQuestions: number;
  attemptedQuestions: number;
  qualifiedQuestions: number;
  unqualifiedQuestions: number;
  violationsCount: number;
  violations: {
    fullscreenExit: number;
    keyboardShortcut: number;
    cursorFocusSwitch: number;
    multipleTabs: number;
    refreshPage: number;
    screenRecordingStopped: number;
    missedPings: number;
  };
  hasViolations: boolean;
  updatedAt: string;
  isPending: boolean;
  session: Session | null;
};

// Helper to get violations from session
const getViolationsFromSession = (session: Session) => {
  const activityLogs = session.activityLogs || [];

  const violations = {
    fullscreenExit: activityLogs.filter(
      (log) =>
        log.event === "fullscreen_exit" ||
        log.event === "fullscreen_exit_attempt",
    ).length,
    keyboardShortcut: activityLogs.filter(
      (log) => log.event === "keyboard_shortcut",
    ).length,
    cursorFocusSwitch: activityLogs.filter(
      (log) => log.event === "cursor_focus_switch",
    ).length,
    multipleTabs: activityLogs.filter((log) => log.event === "multiple_tabs")
      .length,
    refreshPage: activityLogs.filter((log) => log.event === "refresh_page")
      .length,
    screenRecordingStopped: activityLogs.filter(
      (log) => log.event === "screen_recording_stopped",
    ).length,
    missedPings: session.missedPings || 0,
  };

  const totalCount = Object.values(violations).reduce(
    (sum, count) => sum + count,
    0,
  );
  return { violations, totalCount };
};

// Violations popover content component
const ViolationsPopoverContent: React.FC<{
  violations: TableRoundData["violations"];
}> = ({ violations }) => (
  <div style={{ minWidth: 200 }}>
    {violations.fullscreenExit > 0 && (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 4,
        }}
      >
        <Typography.Text>Fullscreen Exit</Typography.Text>
        <Tag color="error">{violations.fullscreenExit}</Tag>
      </div>
    )}
    {violations.keyboardShortcut > 0 && (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 4,
        }}
      >
        <Typography.Text>Keyboard Shortcut</Typography.Text>
        <Tag color="warning">{violations.keyboardShortcut}</Tag>
      </div>
    )}
    {violations.cursorFocusSwitch > 0 && (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 4,
        }}
      >
        <Typography.Text>Focus Switch</Typography.Text>
        <Tag color="warning">{violations.cursorFocusSwitch}</Tag>
      </div>
    )}
    {violations.multipleTabs > 0 && (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 4,
        }}
      >
        <Typography.Text>Multiple Tabs</Typography.Text>
        <Tag color="warning">{violations.multipleTabs}</Tag>
      </div>
    )}
    {violations.refreshPage > 0 && (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 4,
        }}
      >
        <Typography.Text>Page Refresh</Typography.Text>
        <Tag color="error">{violations.refreshPage}</Tag>
      </div>
    )}
    {violations.screenRecordingStopped > 0 && (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 4,
        }}
      >
        <Typography.Text>Recording Stopped</Typography.Text>
        <Tag color="error">{violations.screenRecordingStopped}</Tag>
      </div>
    )}
    {violations.missedPings > 0 && (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 4,
        }}
      >
        <Typography.Text>Missed Pings</Typography.Text>
        <Tag color="warning">{violations.missedPings}</Tag>
      </div>
    )}
  </div>
);

const InterviewProgress: React.FC<InterviewProgressProps> = ({
  interviewSessions,
  interviewRounds,
  setActiveTabKey,
  jobPost,
  profile,
  setSelectedSession,
  jobApplication,
  handleMoveToAIAssessmentRound,
  handleRestartAIAssessmentRound,
  handleSendReminderNotification,
}) => {
  const { org } = useOrganization();
  const [viewMode, setViewMode] = React.useState<"table" | "card">("card");
  const isInterviewRoundsAvailable = jobPost?.interviewRounds?.length > 0;
  const [
    showApplicationLimitReachedWarning,
    setShowApplicationLimitReachedWarning,
  ] = useState<boolean>(false);

  const { currentSubscription, currentLimits } = useAccess();

  // Prepare data for useAssessmentStatistics hook — exclude "profile-match" (shown separately)
  const jobPostInterviewRounds = React.useMemo(
    () =>
      jobPost?.interviewRounds
        ?.filter(
          (r) =>
            r.interviewRoundId !== null &&
            r.interviewRoundId !== "profile-match",
        )
        .map((r) => ({
          interviewRoundId: r.interviewRoundId as string,
          interviewRoundNumber: r.interviewRoundNumber,
        }))
        .sort((a, b) => a.interviewRoundNumber - b.interviewRoundNumber) || [],
    [jobPost?.interviewRounds],
  );

  // Exclude profile-match sessions — they are shown in the ProfileMatch section, not here
  const assessmentSessions = React.useMemo(
    () =>
      interviewSessions.filter((s) => s.interviewRoundId !== "profile-match"),
    [interviewSessions],
  );

  const { statistics, loading: statsLoading } = useAssessmentStatistics({
    interviewSessions: assessmentSessions,
    interviewRounds,
    jobPostInterviewRounds,
  });

  const validateJobApplicationStatus = (
    status: "applied" | "shortlisted" | "interview" | "rejected" | "hired",
  ) => {
    return jobApplication?.status === status;
  };

  const lastSession = [...assessmentSessions].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  )[0];

  const hasJobPostInterviewRounds =
    jobPost?.interviewRounds && jobPost?.interviewRounds.length > 0;

  const isNoRoundGenerated =
    hasJobPostInterviewRounds && assessmentSessions.length === 0;

  // Use single source of truth helper function
  const isProfileMatchDone = isProfileMatchCompleted(jobApplication);

  const isProfileMatchUnqualified = Boolean(
    jobApplication?.profileMatchStatus &&
    jobApplication?.profileMatchStatus?.status === "unqualified",
  );

  // Create unified round data structure that includes ALL configured rounds
  const unifiedRounds: UnifiedRoundData[] = React.useMemo(() => {
    if (!hasJobPostInterviewRounds || !jobPost.interviewRounds) {
      return [];
    }

    // Get all configured rounds from job post, sorted by round number.
    // Exclude "profile-match" — it is shown separately on the Summary tab (ProfileMatch section),
    // not in the Assessment Progress timeline.
    const configuredRounds = jobPost.interviewRounds
      .filter(
        (r) =>
          r.interviewRoundId !== null && r.interviewRoundId !== "profile-match",
      )
      .sort((a, b) => a.interviewRoundNumber - b.interviewRoundNumber);

    // Group assessment sessions by round ID (profile-match already excluded)
    const sessionsByRoundId: Record<string, Session[]> = {};
    assessmentSessions.forEach((session) => {
      if (!session || !session._id || !session.interviewRoundId) return;
      const roundId = session.interviewRoundId;
      if (!sessionsByRoundId[roundId]) {
        sessionsByRoundId[roundId] = [];
      }
      sessionsByRoundId[roundId].push(session);
    });

    // Sort sessions within each round by createdAt
    Object.values(sessionsByRoundId).forEach((sessions) => {
      sessions.sort((a, b) => {
        const timeDiff =
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        if (timeDiff !== 0) return timeDiff;
        return (a._id || "").localeCompare(b._id || "");
      });
    });

    // Build unified round data
    return configuredRounds.map((configRound, index) => {
      const roundId = configRound.interviewRoundId as string;
      const roundSessions = sessionsByRoundId[roundId] || [];
      const latestSession =
        roundSessions.length > 0
          ? roundSessions[roundSessions.length - 1]
          : null;

      // Find round details from interviewRounds.
      // Special round IDs ("profile-match", "overall-assessment") are stored as interviewRoundId
      // in the job post config but the actual InterviewRound documents from getRounds have MongoDB
      // _ids, not these special strings. Fall back to matching by title/category for special rounds.
      const roundDetailsBySpecialId =
        roundId === "profile-match"
          ? interviewRounds.find(
              (r) =>
                r.title?.toLowerCase().trim() === "profile matching" ||
                (r.category || []).some(
                  (cat) =>
                    cat.toLowerCase().replace(/\s+/g, "") === "profilematch",
                ),
            )
          : roundId === "overall-assessment"
            ? interviewRounds.find(
                (r) => r.title?.toLowerCase().trim() === "overall assessment",
              )
            : undefined;
      const roundDetails =
        interviewRounds.find((r) => r._id === roundId) ??
        roundDetailsBySpecialId;

      // Known display names for special rounds when no API document is matched
      const specialRoundTitle =
        roundId === "profile-match"
          ? "Profile Matching"
          : roundId === "overall-assessment"
            ? "Overall Assessment"
            : undefined;

      // Determine status
      let status: "not-attempted" | "pending" | "qualified" | "unqualified" =
        "not-attempted";
      if (latestSession) {
        status =
          latestSession.status === "qualified"
            ? "qualified"
            : latestSession.status === "unqualified"
              ? "unqualified"
              : "pending";
      }

      const attemptNumber =
        roundSessions.length > 0
          ? roundSessions.findIndex((s) => s._id === latestSession?._id) + 1
          : 0;
      const isRestarted = roundSessions.length > 1;

      // displayRoundNumber is 1-based position in the filtered (non-profile-match) list.
      // We do NOT use configRound.interviewRoundNumber directly because profile-match is
      // excluded from display, so the stored numbers (e.g. overall-assessment = 2) would
      // show incorrectly when profile-match (1) is hidden.
      const displayRoundNumber = index + 1;
      return {
        roundId,
        roundNumber: configRound.interviewRoundNumber,
        displayRoundNumber,
        title:
          roundDetails?.title ||
          specialRoundTitle ||
          `Round ${displayRoundNumber}`,
        isMustQualify: configRound.isMustQualify || false,
        passPercentage: configRound.passPercentage,
        sessions: roundSessions,
        latestSession,
        status,
        hasAttempted: roundSessions.length > 0,
        attemptNumber,
        isRestarted,
      };
    });
  }, [
    hasJobPostInterviewRounds,
    jobPost.interviewRounds,
    assessmentSessions,
    interviewRounds,
  ]);

  // Flatten to one item per attempt (session) or one per round when not attempted - for timeline and table
  const attemptItems: AttemptItem[] = React.useMemo(() => {
    const items: AttemptItem[] = [];
    unifiedRounds.forEach((round) => {
      if (round.sessions.length > 0) {
        round.sessions.forEach((session, idx) => {
          const attemptNumber = idx + 1;
          const status = (
            session.status === "qualified"
              ? "qualified"
              : session.status === "unqualified"
                ? "unqualified"
                : "pending"
          ) as AttemptItem["status"];
          items.push({
            roundId: round.roundId,
            displayRoundNumber: round.displayRoundNumber,
            title: round.title,
            session,
            attemptNumber,
            isRestarted: round.sessions.length > 1,
            status,
          });
        });
      } else {
        items.push({
          roundId: round.roundId,
          displayRoundNumber: round.displayRoundNumber,
          title: round.title,
          session: null,
          attemptNumber: 0,
          isRestarted: false,
          status: "not-attempted",
        });
      }
    });
    return items;
  }, [unifiedRounds]);

  // Move to AI Assessment should be enabled when:
  // 1. Profile match is completed (resume uploaded and profile match done)
  // 2. No interview rounds have been generated yet
  // 3. Application status is "applied" (normal flow) OR "rejected" with unqualified profile match (recovery flow)
  const isMoveToAIAssessmentRoundEnabled =
    isProfileMatchDone &&
    isNoRoundGenerated &&
    (validateJobApplicationStatus("applied") ||
      (validateJobApplicationStatus("rejected") && isProfileMatchUnqualified));

  const isRestartAIAssessmentRoundEnabled =
    validateJobApplicationStatus("rejected") &&
    !isNoRoundGenerated &&
    lastSession &&
    lastSession?.status === "unqualified";

  const hasAnyPendingSessions = assessmentSessions.some(
    (session) => session.status === "pending",
  );

  const hasJobPostInterviewRoundsForEmail =
    jobPost?.interviewRounds && jobPost?.interviewRounds.length > 0;

  const shouldShowRoundEmailButton =
    jobPost?.isActive &&
    hasJobPostInterviewRoundsForEmail &&
    interviewSessions.length > 0 &&
    hasAnyPendingSessions &&
    validateJobApplicationStatus("applied");

  const isEmailNotificationEnabled = jobPost?.notifications?.email ?? true;

  const shouldShowWhatsappButton =
    jobPost?.isActive &&
    hasJobPostInterviewRoundsForEmail &&
    interviewSessions.length > 0 &&
    hasAnyPendingSessions &&
    validateJobApplicationStatus("applied");

  const isWhatsappNotificationEnabled =
    (jobPost?.notifications?.whatsapp ?? true) &&
    (profile?.basicDetails.whatsAppConsent?.consent ?? true);

  const handleSendReminderClick = () => {
    const willSendEmail = shouldShowRoundEmailButton && isEmailNotificationEnabled;
    const willSendWhatsapp = shouldShowWhatsappButton && isWhatsappNotificationEnabled;

    if (!willSendEmail && !willSendWhatsapp) return;

    const content =
      willSendEmail && willSendWhatsapp
        ? "Are you sure you want to send an email and WhatsApp message to the candidate reminding them to complete the interview round?"
        : willSendEmail
          ? "Are you sure you want to send an email to the candidate reminding them to complete the interview round?"
          : "Are you sure you want to send a WhatsApp message to the candidate reminding them to complete the interview round?";

    Modal.confirm({
      centered: true,
      okText: "Send",
      cancelText: "Cancel",
      icon: null,
      title: "Send Reminder?",
      content,
      onOk: async () => {
        await handleSendReminderNotification("round");
      },
      onCancel: () => {
        return;
      },
    });
  };

  const handleViewDetails = (sessionId: string) => {
    const session = interviewSessions.find(
      (session) => session._id === sessionId,
    );
    if (session) {
      setSelectedSession(session);
      // Store roundId and sessionId in sessionStorage so Assessments tab selects the same round/session
      sessionStorage.setItem("selectedRoundId", session.interviewRoundId);
      sessionStorage.setItem("selectedSessionId", sessionId);
      setActiveTabKey("assessments");
    }
  };

  // Group assessment sessions by interviewRoundId to track attempts (for timeline view)
  const sessionsByRoundForTimeline: Record<string, Session[]> = {};
  assessmentSessions.forEach((session) => {
    if (!session || !session._id) return;
    const roundId = session.interviewRoundId;
    if (!sessionsByRoundForTimeline[roundId]) {
      sessionsByRoundForTimeline[roundId] = [];
    }
    sessionsByRoundForTimeline[roundId].push(session);
  });

  // Sort each round's sessions by createdAt to determine attempt order
  Object.values(sessionsByRoundForTimeline).forEach((sessions) => {
    sessions.sort((a, b) => {
      const timeDiff =
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      if (timeDiff !== 0) return timeDiff;
      return (a._id || "").localeCompare(b._id || "");
    });
  });

  const getStatusConfig = (status: SessionStatus | "not-attempted") => {
    switch (status) {
      case "qualified":
        return {
          color: "success",
          icon: <CheckCircleOutlined />,
          text: "Qualified",
          description: "The candidate has completed this round and qualified.",
        };
      case "unqualified":
        return {
          color: "error",
          icon: <CloseCircleOutlined />,
          text: "Unqualified",
          description: "The candidate has not qualified this round.",
        };
      case "not-attempted":
        return {
          color: "default",
          icon: <ClockCircleOutlined />,
          text: "Pending",
          description: "This round has not been attempted yet.",
        };
      default:
        return {
          color: "default",
          icon: <ClockCircleOutlined />,
          text: "Pending",
          description: "Waiting for the round to be completed.",
        };
    }
  };

  const handleActionWithConfirmation = (
    action: "move.to.ai.assessment.round" | "restart.ai.assessment.round",
  ) => {
    let confirmationContent: React.ReactNode = null;
    let okButtonText = "Confirm";

    if (!currentSubscription || !currentLimits) {
      return;
    }

    if (currentSubscription.status === "inactive") {
      openNotification(
        "error",
        "Your subscription is inactive. Please upgrade your subscription to perform this action.",
        "",
      );
      return;
    }

    const isApplicationLimitReached =
      currentSubscription.measurements.applicationsProcessed >=
      currentLimits.applicationsProcessed;

    if (isApplicationLimitReached) {
      setShowApplicationLimitReachedWarning(true);
      return;
    }

    switch (action) {
      case "move.to.ai.assessment.round":
        okButtonText = "Move to AI Assessment";
        confirmationContent = (
          <div>
            <p style={{ marginBottom: "12px", fontWeight: 500 }}>
              This action will:
            </p>
            <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
              <li>Move the application to the AI assessment round</li>
              <li>Generate interview sessions for the candidate to complete</li>
              <li>Permanently change the application status</li>
              <li>
                Send an email notification to the candidate about the assessment
              </li>
            </ul>
            <p style={{ marginBottom: 0, color: "var(--ant-color-error)" }}>
              <strong>Note:</strong> This action cannot be undone.
            </p>
          </div>
        );
        break;
      case "restart.ai.assessment.round":
        okButtonText = "Restart AI Assessment";
        confirmationContent = (
          <div>
            <p style={{ marginBottom: "12px", fontWeight: 500 }}>
              This action will:
            </p>
            <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
              <li>Restart the AI assessment round for this candidate</li>
              <li>Reset all previous assessment data for this round</li>
              <li>Require the candidate to complete the assessment again</li>
              <li>
                Send an email notification to the candidate about the restart
              </li>
            </ul>
            <p style={{ marginBottom: 0, color: "var(--ant-color-error)" }}>
              <strong>Note:</strong> This action cannot be undone.
            </p>
          </div>
        );
        break;
      default:
        break;
    }

    if (confirmationContent) {
      Modal.confirm({
        title: "Confirm Action",
        content: confirmationContent,
        okText: okButtonText,
        cancelText: "Cancel",
        icon: null,
        centered: true,
        okButtonProps: {
          style: {
            backgroundColor:
              org?.theme?.primaryColor || "var(--ant-color-primary)",
            borderColor: org?.theme?.primaryColor || "var(--ant-color-primary)",
          },
        },
        onOk: async () => {
          if (action === "move.to.ai.assessment.round") {
            await handleMoveToAIAssessmentRound();
          } else if (action === "restart.ai.assessment.round") {
            await handleRestartAIAssessmentRound();
          }
        },
        onCancel: () => {
          return;
        },
      });
    }
  };

  const getTimeLineChildren = (attempt: AttemptItem) => {
    const {
      status,
      session,
      title,
      displayRoundNumber,
      attemptNumber,
      isRestarted,
    } = attempt;
    const statusConfig = getStatusConfig(status);
    const sessionId = session?._id || "";
    const sessionUpdatedAt = session?.updatedAt
      ? typeof session.updatedAt === "string"
        ? session.updatedAt
        : session.updatedAt.toString()
      : "";

    // Determine which actions to show for this card
    const isLastUnqualifiedSession =
      lastSession?._id === sessionId &&
      status === "unqualified" &&
      validateJobApplicationStatus("rejected");

    // Show "Move to AI Assessment" on first round if not attempted and enabled (only for not-attempted item)
    const shouldShowMoveToAI =
      displayRoundNumber === 1 &&
      status === "not-attempted" &&
      isMoveToAIAssessmentRoundEnabled;

    const shouldShowRestartAI =
      isRestartAIAssessmentRoundEnabled && isLastUnqualifiedSession;

    const cardActions = [];

    // Add "Move to AI Assessment Round" if conditions are met
    if (shouldShowMoveToAI) {
      cardActions.push(
        <Button
          key="move-to-ai"
          type="primary"
          icon={<RightCircleOutlined />}
          onClick={() =>
            handleActionWithConfirmation("move.to.ai.assessment.round")
          }
          disabled={!isMoveToAIAssessmentRoundEnabled}
        >
          {jobApplication?.questionnaireStatus === "action_required"
            ? "Send Assessment"
            : "Move to AI Assessment"}
        </Button>,
      );
    }

    // Add "Restart AI Assessment Round" if conditions are met
    if (shouldShowRestartAI) {
      cardActions.push(
        <Button
          key="restart-ai"
          type="primary"
          icon={<ReloadOutlined />}
          onClick={() =>
            handleActionWithConfirmation("restart.ai.assessment.round")
          }
          disabled={!isRestartAIAssessmentRoundEnabled}
        >
          Restart AI Assessment
        </Button>,
      );
    }

    // Add combined "Send Reminder" if any reminder channel is available for this pending session
    if (status === "pending" && (shouldShowRoundEmailButton || shouldShowWhatsappButton)) {
      const willSendEmail = shouldShowRoundEmailButton && isEmailNotificationEnabled;
      const willSendWhatsapp = shouldShowWhatsappButton && isWhatsappNotificationEnabled;
      const isDisabled = !willSendEmail && !willSendWhatsapp;

      cardActions.push(
        <Tooltip
          key="send-reminder"
          title={
            isDisabled
              ? "Please enable notifications in JobPost or check candidate WhatsApp consent"
              : ""
          }
        >
          <Button
            type="primary"
            icon={<MailOutlined />}
            disabled={isDisabled}
            onClick={handleSendReminderClick}
          >
            Send Reminder
          </Button>
        </Tooltip>,
      );
    }

    // Add "View Details" only if session exists
    if (session) {
      cardActions.push(
        <Button
          key="view-details"
          type="default"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(sessionId)}
        >
          View Details
        </Button>,
      );
    }

    // Determine background color based on status
    const getBackgroundColor = () => {
      switch (status) {
        case "qualified":
          return "rgba(82, 196, 26, 0.08)"; // success background
        case "unqualified":
          return "rgba(255, 77, 79, 0.08)"; // error background
        case "pending":
          return "rgba(250, 173, 20, 0.08)"; // warning background
        default:
          return "transparent"; // not-attempted: default
      }
    };

    return (
      <Card
        className="interview-progress-timeline-card"
        size="small"
        style={{
          marginBottom: "4px",
          borderLeft: `4px solid ${
            status === "qualified"
              ? "var(--ant-color-success)"
              : status === "unqualified"
                ? "var(--ant-color-error)"
                : status === "pending"
                  ? "var(--ant-color-warning)"
                  : "var(--ant-color-border-secondary)"
          }`,
          backgroundColor: getBackgroundColor(),
        }}
      >
        <div className="interview-progress-timeline-card-content">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "4px",
            }}
          >
            <div>
              <Typography.Title
                level={5}
                className="card-title"
                style={{ marginBottom: 0 }}
              >
                Round {displayRoundNumber}: {title}
              </Typography.Title>
              {/* Show attempt number for every attempt when round has multiple attempts */}
              {(isRestarted || attemptNumber > 0) && session && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Attempt {attemptNumber}
                </Typography.Text>
              )}
            </div>
            <Tag
              color={statusConfig.color}
              icon={statusConfig.icon}
              className="card-tag"
              style={{ margin: 0 }}
            >
              {statusConfig.text}
            </Tag>
          </div>
          {sessionUpdatedAt && (
            <Typography.Text
              type="secondary"
              className="card-text"
              style={{ display: "block", marginBottom: "4px" }}
            >
              {dayjs(sessionUpdatedAt).format("DD MMM YYYY, hh:mm A")}
            </Typography.Text>
          )}
          <Typography.Paragraph
            type="secondary"
            className=""
            style={{ margin: 0, marginBottom: "8px" }}
          >
            {statusConfig.description}
          </Typography.Paragraph>
          <div className="interview-progress-card-actions">{cardActions}</div>
        </div>
      </Card>
    );
  };

  // Create timeline items - one per attempt (session) or per not-attempted round
  const timeLineItems = attemptItems.map((attempt) => ({
    children: getTimeLineChildren(attempt),
  }));

  // Table data - one row per attempt (session) or per not-attempted round
  const tableData: TableRoundData[] = React.useMemo(() => {
    if (!hasJobPostInterviewRounds) return [];

    return attemptItems.map((attempt) => {
      const session = attempt.session;
      const { violations, totalCount } = session
        ? getViolationsFromSession(session)
        : {
            violations: {
              fullscreenExit: 0,
              keyboardShortcut: 0,
              cursorFocusSwitch: 0,
              multipleTabs: 0,
              refreshPage: 0,
              screenRecordingStopped: 0,
              missedPings: 0,
            },
            totalCount: 0,
          };

      const roundStats = session
        ? statistics?.rounds?.find((r) => r.sessionId === session._id)
        : null;

      return {
        key: session ? session._id : `not-attempted-${attempt.roundId}`,
        sessionId: session?._id || "",
        roundName: attempt.title,
        roundNumber: attempt.displayRoundNumber,
        displayRoundNumber: attempt.displayRoundNumber,
        interviewRoundId: attempt.roundId,
        attemptNumber: attempt.attemptNumber,
        isRestarted: attempt.isRestarted,
        status:
          attempt.status === "not-attempted"
            ? "not-attempted"
            : (attempt.status as SessionStatus),
        totalQuestions:
          roundStats?.totalQuestions ?? session?.noOfQuestionsGenerated ?? 0,
        attemptedQuestions: roundStats?.attemptedQuestions ?? 0,
        qualifiedQuestions: roundStats?.qualifiedQuestions ?? 0,
        unqualifiedQuestions: roundStats?.unqualifiedQuestions ?? 0,
        violationsCount: totalCount,
        violations,
        hasViolations: totalCount > 0,
        updatedAt: session?.updatedAt
          ? typeof session.updatedAt === "string"
            ? session.updatedAt
            : session.updatedAt.toString()
          : "",
        isPending: attempt.status === "pending",
        session,
      };
    });
  }, [attemptItems, statistics, hasJobPostInterviewRounds]);

  // Table columns with Column Groups
  const tableColumns: ColumnsType<TableRoundData> = [
    {
      title: "Round",
      dataIndex: "roundName",
      key: "roundName",
      fixed: "left",
      width: 140,
      ellipsis: true,
      render: (text: string, record) => {
        // Determine background color based on status
        const getBackgroundColor = () => {
          switch (record.status) {
            case "qualified":
              return "rgba(82, 196, 26, 0.08)"; // success background
            case "unqualified":
              return "rgba(255, 77, 79, 0.08)"; // error background
            case "pending":
              return "rgba(250, 173, 20, 0.08)"; // warning background
            default:
              return "transparent"; // not-attempted: default
          }
        };

        return (
          <Tooltip title={text} placement="topLeft">
            <div
              style={{
                backgroundColor: getBackgroundColor(),
                padding: "4px 8px",
                borderRadius: "4px",
              }}
            >
              <Typography.Text strong style={{ display: "block" }} ellipsis>
                Round {record.displayRoundNumber}: {text}
              </Typography.Text>
              {record.isRestarted && (
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  Attempt {record.attemptNumber}
                </Typography.Text>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status: SessionStatus | "not-attempted") => {
        const config = getStatusConfig(status);
        return (
          <Tooltip title={config.description}>
            <Tag
              color={config.color}
              icon={config.icon}
              style={{ cursor: "help" }}
            >
              {config.text}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Questions",
      children: [
        {
          title: "Total",
          dataIndex: "totalQuestions",
          key: "totalQuestions",
          width: 60,
          align: "center" as const,
          render: (count: number) => <Tag color="default">{count}</Tag>,
        },
        // {
        //   title: "Ans",
        //   dataIndex: "attemptedQuestions",
        //   key: "attemptedQuestions",
        //   width: 60,
        //   align: "center" as const,
        //   render: (count: number, record: TableRoundData) => (
        //     <Tag color={record.isPending ? "default" : "processing"}>{count}</Tag>
        //   ),
        // },
        {
          title: "✓",
          dataIndex: "qualifiedQuestions",
          key: "qualifiedQuestions",
          width: 50,
          align: "center" as const,
          render: (count: number, record: TableRoundData) => {
            if (record.isPending || record.status === "not-attempted") {
              return <Typography.Text type="secondary">-</Typography.Text>;
            }
            return <Tag color="success">{count}</Tag>;
          },
        },
        {
          title: "✗",
          dataIndex: "unqualifiedQuestions",
          key: "unqualifiedQuestions",
          width: 50,
          align: "center" as const,
          render: (count: number, record: TableRoundData) => {
            if (record.isPending || record.status === "not-attempted") {
              return <Typography.Text type="secondary">-</Typography.Text>;
            }
            return <Tag color="error">{count}</Tag>;
          },
        },
      ],
    },
    {
      title: "Violations",
      dataIndex: "violationsCount",
      key: "violations",
      width: 90,
      align: "center" as const,
      render: (count: number, record: TableRoundData) => {
        if (
          record.isPending ||
          record.status === "not-attempted" ||
          count === 0
        ) {
          return <Typography.Text type="secondary">-</Typography.Text>;
        }
        return (
          <Popover
            content={
              <ViolationsPopoverContent violations={record.violations} />
            }
            title="Violation Breakdown"
            trigger="click"
            placement="bottom"
          >
            <Badge
              count={count}
              style={{ backgroundColor: "#faad14", cursor: "pointer" }}
            >
              <Button
                type="text"
                size="small"
                icon={<WarningOutlined style={{ color: "#faad14" }} />}
              />
            </Badge>
          </Popover>
        );
      },
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 140,
      render: (date: string, record: TableRoundData) => {
        if (record.isPending || !date) {
          return <Typography.Text type="secondary">-</Typography.Text>;
        }
        return (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(date).format("DD MMM, hh:mm A")}
          </Typography.Text>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 50,
      align: "center" as const,
      render: (_: unknown, record: TableRoundData) => {
        const isLastUnqualified =
          lastSession?._id === record.sessionId &&
          record.status === "unqualified" &&
          validateJobApplicationStatus("rejected");

        const menuItems: {
          key: string;
          label: string;
          icon: React.ReactNode;
          onClick: () => void;
          disabled?: boolean;
          title?: string;
        }[] = [];

        if (record.session) {
          menuItems.push({
            key: "view",
            label: "View Details",
            icon: <EyeOutlined />,
            onClick: () => handleViewDetails(record.sessionId),
          });
        }

        if (record.isPending && (shouldShowRoundEmailButton || shouldShowWhatsappButton)) {
          const willSendEmail = shouldShowRoundEmailButton && isEmailNotificationEnabled;
          const willSendWhatsapp = shouldShowWhatsappButton && isWhatsappNotificationEnabled;
          menuItems.push({
            key: "reminder",
            label: "Send Reminder",
            icon: <MailOutlined />,
            onClick: handleSendReminderClick,
            disabled: !willSendEmail && !willSendWhatsapp,
            title:
              !willSendEmail && !willSendWhatsapp
                ? "Please enable notifications in JobPost or check candidate WhatsApp consent"
                : undefined,
          });
        }

        if (isRestartAIAssessmentRoundEnabled && isLastUnqualified) {
          menuItems.push({
            key: "restart",
            label: "Restart Assessment",
            icon: <ReloadOutlined />,
            onClick: () =>
              handleActionWithConfirmation("restart.ai.assessment.round"),
          });
        }

        if (menuItems.length === 0) {
          return null;
        }

        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <Button type="text" size="small" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="job-application-summary-content-card">
      <div className="job-application-summary-content-card-header">
        <Typography.Title level={5} style={{ margin: 0 }}>
          <FieldTimeOutlined style={{ color: "var(--ant-color-primary)" }} />
          &nbsp; Assessment Progress
        </Typography.Title>
        <Radio.Group
          value={viewMode}
          onChange={(e) => setViewMode(e.target.value)}
          optionType="button"
          buttonStyle="solid"
          size="small"
        >
          <Radio.Button value="table">
            <UnorderedListOutlined />
          </Radio.Button>
          <Radio.Button value="card">
            <AppstoreOutlined />
          </Radio.Button>
        </Radio.Group>
      </div>

      <div className="interview-progress-content-container">
        {!isInterviewRoundsAvailable && (
          <div className="summary-card-placeholder">
            <Empty
              description="There are no assessments configured for this job post."
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        )}

        {isInterviewRoundsAvailable && attemptItems.length > 0 && (
          <>
            {viewMode === "card" && (
              <div className="interview-progress-time-line-container">
                <Timeline items={timeLineItems} />
              </div>
            )}

            {viewMode === "table" && (
              <div className="interview-progress-table-container">
                <Table
                  columns={tableColumns}
                  dataSource={tableData}
                  pagination={false}
                  size="small"
                  bordered
                  loading={statsLoading}
                  rowClassName={(record) => {
                    switch (record.status) {
                      case "qualified":
                        return "interview-progress-table-row--qualified";
                      case "unqualified":
                        return "interview-progress-table-row--unqualified";
                      case "pending":
                        return "interview-progress-table-row--pending";
                      default:
                        return "";
                    }
                  }}
                  //scroll={{ x: 700, y: "calc(100vh - 500px)" }}
                />
              </div>
            )}
          </>
        )}
      </div>

      <SubscriptionWarning
        open={showApplicationLimitReachedWarning}
        handleClose={() => setShowApplicationLimitReachedWarning(false)}
        title="Assessment Limit Reached"
        type="job-application-limit"
      />
    </div>
  );
};

export default InterviewProgress;
